import React, {useEffect, useState} from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import {DateTimePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {NextSyncOffset, SyncTarget} from '../types';
import {updateSyncTarget} from '../api/dataApi';
import {SnackFn} from './CreateSyncTargetDialog';

interface EditSyncTargetDialogProps {
  open: boolean;
  item: SyncTarget | null;
  onClose: () => void;
  onUpdated: () => Promise<void> | void;
  showSnack: SnackFn;
}

export default function EditSyncTargetDialog(props: EditSyncTargetDialogProps) {
  const { open, item, onClose, onUpdated, showSnack } = props;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{ description?: string; enabled?: boolean; nextSync?: string; nextSyncOffset?: NextSyncOffset }>({});

  useEffect(() => {
    if (item) {
      setForm({
        description: item.description || '',
        enabled: item.enabled,
        nextSync: item.nextSync ? dayjs(item.nextSync).toISOString() : undefined,
        nextSyncOffset: (item.nextSyncOffset as NextSyncOffset) || '1M',
      });
    }
  }, [item]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit sync target</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Description"
              value={form.description ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>Enabled</Typography>
              <Switch
                checked={!!form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                disabled={saving}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="edit-offset-label">Next sync offset</InputLabel>
              <Select
                labelId="edit-offset-label"
                label="Next sync offset"
                value={form.nextSyncOffset || '1M'}
                onChange={(e) => setForm((f) => ({ ...f, nextSyncOffset: e.target.value as NextSyncOffset }))}
              >
                <MenuItem value="1D">1D</MenuItem>
                <MenuItem value="1W">1W</MenuItem>
                <MenuItem value="1M">1M</MenuItem>
                <MenuItem value="1Y">1Y</MenuItem>
                <MenuItem value="14D">14D</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DateTimePicker
              label="Next sync"
              value={form.nextSync ? dayjs(form.nextSync) : null}
              onChange={(val) => setForm((f) => ({ ...f, nextSync: val ? dayjs(val).toISOString() : undefined }))}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={async () => {
            if (!item) return;
            try {
              setSaving(true);
              const payload: any = {
                description: form.description ?? undefined,
                enabled: form.enabled,
                nextSync: form.nextSync ? new Date(form.nextSync).toISOString() : undefined,
                nextSyncOffset: form.nextSyncOffset,
              };
              await updateSyncTarget(item.id, payload);
              showSnack({ open: true, message: 'Sync target updated', severity: 'success' });
              onClose();
              await onUpdated();
            } catch (err) {
              showSnack({ open: true, message: 'Failed to update sync target', severity: 'error' });
            } finally {
              setSaving(false);
            }
          }}
          variant="contained"
          disabled={saving}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
